import React from "react";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: React.ReactNode;
    hint?: React.ReactNode;
    error?: React.ReactNode;
    /** Id of the hint element, for aria-describedby. */
    hintId?: string;
    /** Id of the error element, for aria-describedby. */
    errorId?: string;
    /** Leave the hint in the document when an error is showing. */
    persistHint?: boolean;
    leftIcon?: React.ComponentType<{ className?: string }>;
    rightAddon?: React.ReactNode;
    containerClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ label, hint, error, hintId, errorId, persistHint = false, leftIcon: LeftIcon, rightAddon, className = "", containerClassName = "", id, ...props }, ref) => {
        const inputId = id || (props.name ? `input-${props.name}` : undefined);
        const baseClasses = "w-full px-3 py-2 text-sm border rounded-button bg-white text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 transition-colors duration-150";
        const stateClasses = error ? "border-red-300 focus:border-red-400 focus:ring-red-200" : "border-brand-line focus:border-brand-orange";
        const paddingLeft = LeftIcon ? "pl-9" : "";
        const paddingRight = rightAddon ? "pr-10" : "";

        return (
            <div className={containerClassName}>
                {label && (
                    <label htmlFor={inputId} className="block text-sm font-semibold text-brand-ink mb-1.5">
                        {label}
                    </label>
                )}
                <div className="relative">
                    {LeftIcon && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-ink/40 pointer-events-none">
                            <LeftIcon className="h-4 w-4" />
                        </span>
                    )}
                    <input ref={ref} id={inputId} className={`${baseClasses} ${stateClasses} ${paddingLeft} ${paddingRight} ${className}`} {...props} />
                    {rightAddon && <div className="absolute inset-y-0 right-0 flex items-center pr-2.5">{rightAddon}</div>}
                </div>
                {hint && (persistHint || !error) && (
                    <p id={hintId} className="mt-1 text-xs text-brand-ink/60">
                        {hint}
                    </p>
                )}
                {error && (
                    <p id={errorId} className="mt-1 text-xs text-red-600">
                        {error}
                    </p>
                )}
            </div>
        );
    },
);

Input.displayName = "Input";

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    label?: React.ReactNode;
    hint?: React.ReactNode;
    error?: React.ReactNode;
    containerClassName?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ label, hint, error, className = "", containerClassName = "", id, ...props }, ref) => {
        const textareaId = id || (props.name ? `textarea-${props.name}` : undefined);
        const baseClasses = "w-full px-3 py-2 text-sm border rounded-button bg-white text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 transition-colors duration-150 resize-y";
        const stateClasses = error ? "border-red-300 focus:border-red-400 focus:ring-red-200" : "border-brand-line focus:border-brand-orange";

        return (
            <div className={containerClassName}>
                {label && (
                    <label htmlFor={textareaId} className="block text-sm font-semibold text-brand-ink mb-1.5">
                        {label}
                    </label>
                )}
                <textarea ref={ref} id={textareaId} className={`${baseClasses} ${stateClasses} ${className}`} {...props} />
                {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
                {!error && hint && <p className="mt-1 text-xs text-brand-ink/60">{hint}</p>}
            </div>
        );
    },
);

Textarea.displayName = "Textarea";

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: React.ReactNode;
    hint?: React.ReactNode;
    error?: React.ReactNode;
    containerClassName?: string;
    children: React.ReactNode;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    ({ label, hint, error, className = "", containerClassName = "", id, children, ...props }, ref) => {
        const selectId = id || (props.name ? `select-${props.name}` : undefined);
        const baseClasses = "w-full px-3 py-2 text-sm border rounded-button bg-white text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-orange/40 transition-colors duration-150";
        const stateClasses = error ? "border-red-300 focus:border-red-400 focus:ring-red-200" : "border-brand-line focus:border-brand-orange";

        return (
            <div className={containerClassName}>
                {label && (
                    <label htmlFor={selectId} className="block text-sm font-semibold text-brand-ink mb-1.5">
                        {label}
                    </label>
                )}
                <select ref={ref} id={selectId} className={`${baseClasses} ${stateClasses} ${className}`} {...props}>
                    {children}
                </select>
                {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
                {!error && hint && <p className="mt-1 text-xs text-brand-ink/60">{hint}</p>}
            </div>
        );
    },
);

Select.displayName = "Select";

export { Input, Textarea, Select };
export default Input;
